import { useEffect, useRef } from "react";
import { useListOrders, customFetch } from "@workspace/api-client-react";
import { useQuery } from "@tanstack/react-query";
import { useThermalPrinter } from "@/hooks/use-thermal-printer";
import { orderToReceipt, formatReceiptBytes } from "@/lib/receipt-formatter";

// Janela recente pro drain WebUSB: o cupom e montado a partir desta lista
// (generateReceiptData busca o pedido aqui pelo id). 6h — nao 10min — pra
// cobrir pedido que ficou na fila com a impressora ocupada/offline e so drenou
// depois; com janela curta o generateReceiptData devolvia null e o cupom nao
// saia. Ainda e minusculo vs o historico inteiro (sem avatar, event-driven)
// que estourava o egress (ver use-orders-realtime).
const RECENT_WINDOW_MS = 6 * 60 * 60 * 1000;

// Este hook NAO enfileira pedido novo em /api/print-queue — o enqueue de
// new_order e exclusivo do servidor, na criacao do pedido (catalog/orders.ts
// pro link publico, orders.ts pro PDV), atras do mesmo gate printerConnectedAt.
// Enfileirar daqui tambem DUPLICAVA o cupom: com o push WS o agente imprime e
// da ack em <1s, o ack deleta a linha de print_queue, e o unique(store_id,
// order_id) deixa de segurar o segundo insert — o poll de 15s deste hook
// reinseria o pedido ja impresso (incidente 10-11/06/2026, loja e7f7acb4).
// O que sobra aqui e manter generateReceiptData alimentado: o drainQueue do
// useThermalPrinter (WebUSB no navegador) precisa montar os bytes do cupom a
// partir do orderId quando consome a fila enfileirada pelo servidor.
export function useAutoPrintWatcher() {
  // Sem polling: o use-orders-realtime (WS no AdminLayout) invalida esta query
  // quando entra pedido novo; o backstop de 60s cobre o resto.
  const sinceRef = useRef(new Date(Date.now() - RECENT_WINDOW_MS).toISOString());
  const { data: orders = [] } = useListOrders({ dateFrom: sinceRef.current });
  const { data: storeSettings } = useQuery<any>({
    queryKey: ["settings"],
    queryFn: () => customFetch("/api/settings"),
    staleTime: 60_000,
  });
  const printer = useThermalPrinter();
  const ordersList: any[] = orders as any[];

  useEffect(() => {
    const storeName = storeSettings?.name || "Minha Loja";
    const hideDailyNumber = storeSettings?.dailyOrderNumbering === false;
    const hideGroupTitles = storeSettings?.showGroupTitles === false;
    const timezone = storeSettings?.timezone ?? null;
    // Largura da loja (pdvSettings) é a fonte; printer.prefs é só fallback legado.
    const storeWidth = storeSettings?.pdvSettings?.paperWidth;
    const pw = storeWidth === "58mm" || storeWidth === "80mm" ? storeWidth : printer.prefs.paperWidth;
    const asciiOnly = printer.isAsciiOnlyPrinter;
    printer.setGenerateReceiptData((orderId: string) => {
      const order = ordersList.find((o: any) => o.id === orderId);
      if (!order) return null;
      const receipt = orderToReceipt(order, { hideDailyNumber, timezone, hideGroupTitles, orderCodeStyle: storeSettings?.orderCodeStyle ?? null, topMargin: storeSettings?.receiptTopMargin ?? null, showPaidCheckbox: storeSettings?.receiptPaidCheckbox === true, receiptFont: storeSettings?.pdvSettings?.receiptFont ?? null, footerMessage: storeSettings?.receiptFooterPrinted ?? null, porCliente: storeSettings?.tableQrPerClient === true });
      return formatReceiptBytes(storeName, receipt, pw, asciiOnly);
    });
  }, [ordersList, storeSettings?.name, storeSettings?.tableQrPerClient, storeSettings?.dailyOrderNumbering, storeSettings?.orderCodeStyle, storeSettings?.receiptTopMargin, storeSettings?.receiptPaidCheckbox, storeSettings?.showGroupTitles, storeSettings?.timezone, storeSettings?.pdvSettings?.paperWidth, printer.prefs.paperWidth, printer.isAsciiOnlyPrinter]);
}
